import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatPercentage } from '@/utils/formatters.ts'
import { RankRow } from './ranking-card.types.ts'
import { RankingCard, RankCardColors } from './ranking-card.tsx'
import { RankingPodium, PodiumItem } from './ranking-podium.tsx'
import { RankingSelfCard } from './ranking-self-card.tsx'
import { useRankCards, usdText } from './use-rank-cards.ts'
import { useRowOutOfView } from '@/common/anim/use-row-out-of-view.ts'
import { IdeyaSpinner } from '@/common/components/ideya-spinner.tsx'
import { themeConstants } from '@/styles/theme-constants.ts'

// CSS variable references rather than themeStyle values: reloadThemeStyle()
// replaces the whole themeStyle object on a toggle, and a colours object derived
// from it would break the memo bail-out of all 153 cards. Variables resolve per
// theme in _colors.scss, so this object can be a constant.
const CARD_COLORS: RankCardColors = {
  cardBg: 'var(--color-bg-container)',
  cardOutline: 'var(--color-primary-border-gold)',
  listCardOutline: 'var(--color-border)',
  selfCardOutline: 'var(--color-primary-border)',
  linkColor: 'var(--book-name-link)',
  tertiaryTextColor: 'var(--color-tertiary-text)',
  tagBg: 'var(--card-more-bg)',
  longBg: 'var(--color-long-bg)',
  longText: 'var(--ag-grid-table-green)',
  shortBg: 'var(--color-short-bg)',
  shortText: 'var(--ag-grid-table-red)',
}

interface OverallRankingCardProps {
  rankingList: RankRow[]
  // Region-wide list for the podium; ignores the firm filter and search.
  podiumList: RankRow[]
  groupByFirm: boolean
  startDate: string | null
  endDate: string | null
  isLoading?: boolean
}

// Container for the mobile card view. Owns self detection and i18n; passes
// fully resolved props to the pure presentational podium/card/self-card
// components.
export const OverallRankingCard: React.FC<OverallRankingCardProps> = ({
  rankingList,
  podiumList,
  groupByFirm,
  startDate,
  endDate,
  isLoading = false,
}) => {
  const { t } = useTranslation()
  const dailyLabel = t('overall_ranking.mobile_card.daily')

  // The podium comes from the region-wide list (podiumList), so whether a
  // podium exists is judged on that list, not on the firm-filtered one.
  const {
    selfRow: podiumSelfRow,
    hasSingleFirst,
    toCardData,
  } = useRankCards({
    rankingList: podiumList,
    groupByFirm,
    startDate,
    endDate,
  })

  const isSelfOnPodium =
    hasSingleFirst && podiumSelfRow != null && podiumSelfRow.rank <= 3

  // The inline self row is the observer target; the floating card mirrors it
  // and surfaces at the bottom whenever this row scrolls out of view.
  const [setSelfRow, selfOutOfView] = useRowOutOfView()

  const podiumItems: PodiumItem[] = useMemo(
    () =>
      hasSingleFirst
        ? podiumList
            .filter((row) => row.rank != null && row.rank <= 3)
            .map((row) => {
              const data = toCardData(row)
              return {
                data,
                returnPctText: formatPercentage(data.returnPct),
                cumulativePnlText: usdText(data.cumulativePnl),
                dailyPnlText: usdText(data.dailyPnl),
              }
            })
        : [],
    [podiumList, toCardData, hasSingleFirst]
  )

  // List cards: ranks 4+ and null-rank rows always; ranks 1-3 too when there
  // is no single first place (no podium), so nothing is dropped. Resolves the
  // card view model and formatted strings once so memoized cards can skip
  // re-rendering rows that didn't change.
  const listCards = useMemo(
    () =>
      rankingList
        .filter((row) =>
          hasSingleFirst ? row.rank == null || row.rank > 3 : true
        )
        .map((row) => {
          const data = toCardData(row)
          return {
            data,
            returnPctText: formatPercentage(data.returnPct),
            cumulativePnlText: usdText(data.cumulativePnl),
            dailyPnlText: usdText(data.dailyPnl),
          }
        }),
    [rankingList, hasSingleFirst, toCardData]
  )

  const colors = CARD_COLORS

  // Resolved view model for the floating card — the same self entry from the
  // list, reused so the pinned copy stays identical to the inline row.
  const selfCard = useMemo(
    () => listCards.find(({ data }) => data.isSelf),
    [listCards]
  )
  // Float only when there is a self row that isn't on the podium.
  const showFloatingSelf = selfCard != null && !isSelfOnPodium

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: themeConstants.sizeXS,
        paddingBottom: themeConstants.sizeXS,
      }}
    >
      {isLoading && (
        <div style={{ textAlign: 'center' }}>
          <IdeyaSpinner />
        </div>
      )}

      {!isLoading && podiumItems.length > 0 && (
        <RankingPodium
          items={podiumItems}
          colors={colors}
          dailyLabel={dailyLabel}
        />
      )}

      {!isLoading &&
        listCards.map(
          ({ data, returnPctText, cumulativePnlText, dailyPnlText }) => {
            // The self row renders inline like any other rank; the wrapping div
            // is the observer target that drives the floating card's visibility.
            if (data.isSelf && !isSelfOnPodium) {
              return (
                <div key={data.key} ref={setSelfRow}>
                  <RankingCard
                    data={data}
                    returnPctText={returnPctText}
                    cumulativePnlText={cumulativePnlText}
                    dailyPnlText={dailyPnlText}
                    dailyLabel={dailyLabel}
                    colors={colors}
                  />
                </div>
              )
            }
            return (
              <RankingCard
                key={data.key}
                data={data}
                returnPctText={returnPctText}
                cumulativePnlText={cumulativePnlText}
                dailyPnlText={dailyPnlText}
                dailyLabel={dailyLabel}
                colors={colors}
              />
            )
          }
        )}

      {!isLoading && showFloatingSelf && selfCard && (
        <RankingSelfCard
          data={selfCard.data}
          returnPctText={selfCard.returnPctText}
          cumulativePnlText={selfCard.cumulativePnlText}
          dailyPnlText={selfCard.dailyPnlText}
          dailyLabel={dailyLabel}
          colors={colors}
          backgroundColor={'var(--content-bg)'}
          visible={selfOutOfView}
        />
      )}

      {!isLoading && rankingList.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            color: 'var(--color-tertiary-text)',
            padding: themeConstants.sizeXXL,
          }}
        >
          {t('overall_ranking.mobile_card.no_data')}
        </div>
      )}
    </div>
  )
}
